import { Box, LinearProgress, Typography } from '@mui/material';

import type {
  MergeCountsDto,
  MergeSection,
} from '../api/mergeResolution.types';
import { progress, sectionHeader } from '../state/labels';
import type { HeaderCounts } from '../state/labels';

interface SectionHeaderProps {
  section: MergeSection;
  counts: MergeCountsDto;
  /**
   * The selected tab's own counts, shown as a second block to the right of the
   * merge-wide one, with its own progress bar.
   */
  inputCounts?: { name: string; counts: HeaderCounts };
  /** Shown bold, right-aligned, at the far right of the header (auto-resolved header only). */
  note?: string[];
}

const HeaderBlock = ({
  section,
  title,
  progressValue,
}: {
  section: MergeSection;
  title: string;
  progressValue: number;
}) => (
  <Box sx={{ display: 'inline-block' }}>
    <Typography variant="subtitle2">{title}</Typography>
    {section === 'needs_decision' && (
      <LinearProgress
        variant="determinate"
        value={progressValue * 100}
        aria-valuenow={Math.round(progressValue * 100)}
        sx={{ mt: 0.5, height: 3 }}
      />
    )}
  </Box>
);

const SectionHeader = ({
  section,
  counts,
  inputCounts,
  note,
}: SectionHeaderProps) => {
  const headerText = sectionHeader(section, counts);
  const progressValue = progress(counts);

  return (
    // Two lines of note need more room than the half-height header gives a
    // single line of title.
    <Box sx={{ width: '100%', minHeight: note ? 44 : undefined }}>
      {/* Each block shrinks to fit, so its progress bar is as wide as the
          title above it. The note is a flex item at the right edge, so it
          wraps rather than overlapping the blocks. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', columnGap: 4 }}>
        <HeaderBlock
          section={section}
          title={headerText}
          progressValue={progressValue}
        />
        {inputCounts && (
          <HeaderBlock
            section={section}
            title={`${inputCounts.name}: ${sectionHeader(
              section,
              inputCounts.counts
            )}`}
            progressValue={progress(inputCounts.counts)}
          />
        )}
        {note && (
          <Box
            sx={{
              ml: 'auto',
              pl: 2,
              textAlign: 'right',
              alignSelf: 'center',
            }}
          >
            {note.map((line) => (
              <Typography
                key={line}
                variant="subtitle2"
                sx={{ fontWeight: 700 }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SectionHeader;
